import { hasAmendsContent } from "./amends-content.js"

export const AMENDS_NOTE_MODE_UNIFIED = "unified"
export const AMENDS_NOTE_MODE_SPLIT = "split"
export const AMENDS_AUDIENCE_INTERNAL = "internal"
export const AMENDS_AUDIENCE_EXTERNAL = "external"

export function normalizeAudienceAmends(note = {}) {
    const legacyContent = normalizeContent(note?.content)
    const internalContent = resolveAudienceContent(note?.internalContent, legacyContent)
    const externalContent = resolveAudienceContent(note?.externalContent, legacyContent)
    const noteMode = deriveNoteMode({ noteMode: note?.noteMode, internalContent, externalContent })
    const content = selectDefaultContent({ content: legacyContent, internalContent, externalContent })

    return {
        content,
        internalContent,
        externalContent,
        noteMode,
        isSplit: noteMode === AMENDS_NOTE_MODE_SPLIT,
    }
}

export function buildAudienceAmendsPayload(note = {}) {
    const normalized = normalizeAudienceAmends(note)

    if (normalized.noteMode === AMENDS_NOTE_MODE_UNIFIED) {
        const unifiedContent = selectDefaultContent(normalized)

        return {
            content: unifiedContent,
            internalContent: unifiedContent,
            externalContent: unifiedContent,
            noteMode: AMENDS_NOTE_MODE_UNIFIED,
        }
    }

    return {
        content: selectDefaultContent(normalized),
        internalContent: normalized.internalContent,
        externalContent: normalized.externalContent,
        noteMode: AMENDS_NOTE_MODE_SPLIT,
    }
}

export function hasAudienceAmendsContent(note = {}) {
    const normalized = normalizeAudienceAmends(note)

    return (
        hasAmendsContent(normalized.content) ||
        hasAmendsContent(normalized.internalContent) ||
        hasAmendsContent(normalized.externalContent)
    )
}

export function getAudienceAmendsContent(note = {}, audience = AMENDS_AUDIENCE_INTERNAL) {
    const normalized = normalizeAudienceAmends(note)

    if (audience === AMENDS_AUDIENCE_EXTERNAL) return normalized.externalContent
    return normalized.internalContent
}

export function getAudienceAmendsContents(note = {}) {
    const normalized = normalizeAudienceAmends(note)
    const contents = [normalized.internalContent]

    if (normalized.isSplit) contents.push(normalized.externalContent)

    return contents.filter(hasAmendsContent)
}

export function isSplitAudienceAmends(note = {}) {
    return normalizeAudienceAmends(note).isSplit
}

function resolveAudienceContent(value, fallback) {
    if (typeof value === "string") return value
    if (value === null) return ""
    return fallback
}

function deriveNoteMode({ noteMode, internalContent, externalContent }) {
    if (noteMode === AMENDS_NOTE_MODE_SPLIT) return AMENDS_NOTE_MODE_SPLIT
    if (comparableContent(internalContent) !== comparableContent(externalContent)) {
        return AMENDS_NOTE_MODE_SPLIT
    }
    return AMENDS_NOTE_MODE_UNIFIED
}

function selectDefaultContent(note = {}) {
    if (hasAmendsContent(note?.internalContent)) return note.internalContent
    if (hasAmendsContent(note?.externalContent)) return note.externalContent
    return normalizeContent(note?.content)
}

function comparableContent(value) {
    return normalizeContent(value).trim()
}

function normalizeContent(value) {
    if (value === "undefined") return ""
    return typeof value === "string" ? value : ""
}
